import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowRight } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { blogPosts } from '../data/blogData';
import Footer from './Footer';

const BlogList: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className={`min-h-screen transition-colors duration-500 ${
      isDark 
        ? 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 text-gray-100'
        : 'bg-gradient-to-br from-slate-200/50 via-slate-100/70 to-slate-200/50 text-slate-800'
    } pt-20 pb-16`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className={`text-4xl md:text-5xl font-light mb-4 ${
            isDark ? 'text-gray-100' : 'text-slate-800'
          }`}>
            Strategic <span className="gradient-text font-medium">Insights</span>
          </h1>
          <p className={`text-lg ${
            isDark ? 'text-gray-300' : 'text-slate-600'
          }`}>
            Thoughts on communications, strategy, and digital innovation
          </p>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <Link
              key={post.id}
              to={`/blog/${post.slug}`}
              className={`group glass border rounded-3xl overflow-hidden transition-all duration-500 hover:scale-105 cursor-pointer block ${
                isDark ? 'glass-dark glass-hover-dark' : 'glass-light glass-hover-light'
              }`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    isDark 
                      ? 'bg-sunrise-orange/20 text-sunrise-orange border border-sunrise-orange/30' 
                      : 'bg-deep-blue/10 text-deep-blue border border-deep-blue/20'
                  }`}>
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className={`text-xl font-semibold mb-3 group-hover:text-sunrise-orange transition-colors duration-300 ${
                  isDark ? 'text-gray-100' : 'text-slate-800'
                }`}>
                  {post.title}
                </h3>
                
                <p className={`text-sm mb-4 leading-relaxed ${
                  isDark ? 'text-gray-300' : 'text-slate-600'
                }`}>
                  {post.excerpt}
                </p>

                {/* Meta Information */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4 text-xs">
                    <div className={`flex items-center gap-1 ${
                      isDark ? 'text-gray-400' : 'text-slate-500'
                    }`}>
                      <Calendar size={14} />
                      <span>{new Date(post.date).toLocaleDateString()}</span>
                    </div>
                    <div className={`flex items-center gap-1 ${
                      isDark ? 'text-gray-400' : 'text-slate-500'
                    }`}>
                      <Clock size={14} />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                </div>

                {/* Author */}
                <div className={`flex items-center gap-2 text-sm ${
                  isDark ? 'text-gray-400' : 'text-slate-500'
                }`}>
                  <User size={16} />
                  <span>{post.author}</span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {post.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className={`px-2 py-1 rounded-full text-xs ${
                        isDark 
                          ? 'bg-gray-800/50 text-gray-300' 
                          : 'bg-gray-100 text-slate-600'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Read More */}
                <div className="flex items-center justify-end mt-6">
                  <div className={`flex items-center gap-2 text-sm font-medium group-hover:text-sunrise-orange transition-colors duration-300 ${
                    isDark ? 'text-gray-300' : 'text-slate-600'
                  }`}>
                    <span>Read More</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Call to Action */}
        {/* <div className="text-center mt-16">
          <div className={`inline-block glass border rounded-2xl px-8 py-6 ${
            isDark ? 'glass-dark' : 'glass-light'
          }`}>
            <h3 className={`text-2xl font-light mb-4 ${
              isDark ? 'text-gray-100' : 'text-slate-800'
            }`}>
              Stay Updated
            </h3>
            <p className={`text-sm mb-6 ${
              isDark ? 'text-gray-300' : 'text-slate-600'
            }`}>
              Subscribe to receive the latest insights on strategic communications
            </p>
            <button className={`glass border px-6 py-3 rounded-full transition-all duration-300 hover:scale-105 font-medium ${
              isDark 
                ? 'glass-dark glass-hover-dark text-gray-100' 
                : 'glass-light glass-hover-light text-slate-700'
            }`}>
              Subscribe to Newsletter
            </button>
          </div>
        </div> */}
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BlogList;
